import React, { useState, useCallback } from 'react';
import { AudienceOption } from '../../../../../../helpers/audiences';
import { ReactComponent as AudienceIcon } from '../../../../../../../assets/icons/audience.svg';
import { ReactComponent as EndUserIcon } from '../../../../../../../assets/icons/endUser.svg';
import { ReactComponent as ArrowDownIcon } from '../../../../../../../assets/icons/arrow-down.svg';
import { WMConditionalEllipsis, WMMenu, WMTooltip } from '@walkme/wm-ui';
import classes from './AudiencesCellItem.module.scss';
import clsx from 'clsx';
import RecipientsListModal from '../../../../../Audiences/RecipientsListModal/RecipientsListModal';
import { ALL_IN_NOTIFICATION_ID } from '../../../../../Audiences/RecipientsListModal/RecipientsModalTitle';
import { useTranslation } from 'react-i18next';
import { CC_PERMISSIONS } from '@communication-center/data-objects';
import { useHasRequiredPermissions } from '../../../../../../infrastructure/Permissions/PermissionsProvider';

const keyPrefix = 'components.notificationsList.notificationsTable.table-parts.audiencesCell.audiencesCellItem';
const keyPrefixUsers = 'components.usersPermissions.usersPermissionsTable';

const getRecipientsCount = (recipientsSize?: number) => `${recipientsSize != null ? ' (' + recipientsSize + ')' : ''}`;

export const AudiencesCellItem = ({
    audiences,
    endUsersIds,
    totalRecipients,
    notificationName,
    notificationId
}: {
    notificationId: string;
    audiences: AudienceOption[] | undefined;
    endUsersIds: string[] | undefined;
    totalRecipients: number;
    notificationName: string;
}) => {
    const { t } = useTranslation('', { keyPrefix });
    const { t: tUsers } = useTranslation('', { keyPrefix: keyPrefixUsers });

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const handleOpen = useCallback<React.MouseEventHandler<HTMLElement>>(event => {
        setIsMenuOpen(true);
        setAnchorEl(event.currentTarget);
    }, []);

    const audiencesForModal = audiences?.map(audience => ({
        id: audience.id,
        title: audience.value
    }));

    const handleMenuItemClick = async (id: string, name: string) => {
        setAnchorEl(null);
        await RecipientsListModal.openModal({ id, audiences: audiencesForModal, endUsersIds, title: name, notificationName });
        setIsMenuOpen(false);
    };
    const handleClose = () => {
        setIsMenuOpen(false);
        setAnchorEl(null);
    };

    audiences = audiences ?? [];
    endUsersIds = endUsersIds ?? [];

    const showDropdown = audiences.length > 1 || endUsersIds.length > 1 || (endUsersIds.length === 1 && audiences.length > 0);
    const dropDownTestId = `${notificationId}-audience-cell-dropdown-toggle`;
    const dropDownMenuItemId = `${notificationId}-audience-cell-dropdown-item`;
    const singleAudienceTestId = `${notificationId}-audience-cell-single-audience`;
    const hasAudiencesPermissions = useHasRequiredPermissions([CC_PERMISSIONS.Audiences]);

    return (
        <div className={clsx([classes.AudienceCellItemContainer, { [classes.dropDownOpen]: isMenuOpen }])}>
            <div className={classes.AudienceCellItem} {...(showDropdown ? { onClick: handleOpen } : {})}>
                {(!audiences || audiences.length === 0) && (!endUsersIds || endUsersIds.length === 0) && (
                    <span
                        className={classes.AudienceName}
                        onClick={async () =>
                            hasAudiencesPermissions &&
                            (await RecipientsListModal.openModal({
                                id: 'all',
                                title: t('allRecipientsText')
                            }))
                        }
                    >
                        <WMConditionalEllipsis value={t('allRecipientsText')} />
                    </span>
                )}
                {audiences && audiences.length > 0 && (
                    <>
                        {audiences.length > 1 ? (
                            <WMTooltip classes={{ tooltip: classes.tooltipPaddings }} title={t('audiences')}>
                                <span className={classes.AudienceIcon}>
                                    <AudienceIcon />
                                    <span className={classes.number}>{audiences.length}</span>
                                </span>
                            </WMTooltip>
                        ) : (
                            <span
                                className={classes.AudienceName}
                                data-testid={singleAudienceTestId}
                                onClick={async () => {
                                    hasAudiencesPermissions &&
                                        (await RecipientsListModal.openModal({
                                            id: audiences![0].id,
                                            title: audiences![0].label
                                        }));
                                }}
                            >
                                <WMConditionalEllipsis value={audiences[0].label} />
                            </span>
                        )}
                    </>
                )}
                {endUsersIds && endUsersIds.length > 0 && (
                    <>
                        {endUsersIds.length > 1 || (audiences && audiences.length > 0) ? (
                            <WMTooltip classes={{ tooltip: classes.tooltipPaddings }} title={t('recipients')}>
                                <span className={classes.EndUserIcon}>
                                    <EndUserIcon />
                                    <span className={classes.number}>{endUsersIds.length}</span>
                                </span>
                            </WMTooltip>
                        ) : (
                            <span className={classes.EndUserName}>
                                <WMConditionalEllipsis value={endUsersIds[0]} />
                            </span>
                        )}
                    </>
                )}
            </div>
            {showDropdown && (
                <>
                    <div className={classes.arrowIcon} onClick={handleOpen}>
                        <ArrowDownIcon data-testid={dropDownTestId} className={clsx([classes.dropDownIcon], { [classes.opened]: isMenuOpen })} />
                    </div>
                    <WMMenu
                        ds2
                        className={classes.menu}
                        classes={{ paper: classes.menuPaper, list: classes.menuItemsList }}
                        align="right"
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                    >
                        <div
                            className={clsx([classes.menuItem, { [classes.menuItemClickable]: hasAudiencesPermissions }])}
                            onClick={() =>
                                hasAudiencesPermissions && handleMenuItemClick(ALL_IN_NOTIFICATION_ID, t('allAudiences', { notificationName }))
                            }
                            data-testid={dropDownMenuItemId}
                        >
                            <WMConditionalEllipsis value={t('all', { totalRecipients, context: totalRecipients === null && 'null' })} />
                        </div>
                        <div className={classes.separator} data-testid={`${dropDownMenuItemId}-separator-top`} />
                        {audiences?.map(audience => (
                            <div
                                key={audience.value}
                                data-testid={dropDownMenuItemId}
                                className={clsx([classes.menuItem, { [classes.menuItemClickable]: hasAudiencesPermissions }])}
                                onClick={() => hasAudiencesPermissions && handleMenuItemClick(audience.id, audience.value)}
                            >
                                <WMConditionalEllipsis value={`${audience.value}${getRecipientsCount(audience.size)}`} />
                            </div>
                        ))}
                        {audiences.length > 0 && endUsersIds.length > 0 && (
                            <div className={classes.separator} data-testid={`${dropDownMenuItemId}-separator-bottom`} />
                        )}
                        {endUsersIds.length > 0 && hasAudiencesPermissions ? (
                            endUsersIds.map(endUser => (
                                <div key={endUser} className={classes.menuItem} data-testid={dropDownMenuItemId}>
                                    <WMConditionalEllipsis value={endUser} />
                                </div>
                            ))
                        ) : endUsersIds.length > 0 ? (
                            <div className={classes.menuItem} data-testid={dropDownMenuItemId}>
                                {endUsersIds.length > 1
                                    ? tUsers('users_other', { count: endUsersIds.length })
                                    : tUsers('users', { count: endUsersIds.length })}
                            </div>
                        ) : null}
                    </WMMenu>
                </>
            )}
        </div>
    );
};
